import React, { useMemo, useState, useCallback } from "react";
import { useTranslation } from "react-i18next";
import { Icon } from "uikit";
import { isObject, isEqual } from "lodash";
import { useDispatch } from "react-redux";

import { Order } from "../../../../../../../../services";
import { MoveToProgressType } from "../../../../../../../../services/OrderSettings";
import { useTypedSelector } from "../../../../../../../../redux/store";
import { ORDERS_TYPES } from "../../../../../../../../redux/constants/OrdersPage";
import orderPage from "../../../../../../../../redux/reducers/OrdersPage";
import { useKeyBind } from "../../../../../../../../hooks";
import { OrderStatus } from "../../../../../../../../types";
import { Divider } from "../../../../../../../../components";
import {
	StyledRow,
	StyledGrid,
} from "../../../../../../../../components/common";
import ArchiveOrderFiltersModal, {
	DEFAUT_VALUEE_FILTERS,
	ArchiveOrderFiltersModalMemo,
} from "../../../../../../../../components/common/Modal/ArchiveOrderFiltersModal";
import { AccessKey } from "../../../../../../../../access";

import ColorButton from "./components/ColorButton";
import Button from "./components/Button";
import SMSButton from "./components/SMSButton";
import PaymentLinkSmsButton from "./components/PaymentLinkSmsButton";
import CallClientButton from "./components/CallClientButton";
import CallExecutorButton from "./components/CallExexcutorButton";
import AutoCallClientButton from "./components/AutoCallClientButton";
import { OrderTransferButtons } from "./components/OrderTransferButtons";

const SecondRow: React.FC<SecondRow.Props> = ({
	onOpenEditOrderModal,
	toggleModalStatus,

	onCloseOrderClick,
	onCloneOrderClick,
	onSetOrderExecutorClick,
	onApproveOrder,
	onRecalculateOrder,
	onRecalculateOrderWithActualRates,
	onRefuseOrder,
	onSendOrderToAir,
	onSendOrderToExecuting,
	onToggleOrderAir,
	onSetCallToClientBeingRequired,
	onSetCallToClientSuccess,
	onSetCallToExecutorBeingRequired,
	onSetCallToExecutorSuccess,
	onShowClientNumberToExecutor,
	onRevertOrder,
}) => {
	const dispatch = useDispatch();
	const { t } = useTranslation();

	const ordersType = useTypedSelector(
		(state) => state.ordersPageReducer.ordersType,
	);
	const language = useTypedSelector((state) => state.session.language);
	const moveToProgress = useTypedSelector(
		(state) => state.settings.order.moveToProgress,
	);
	const activeOrder = useTypedSelector(
		(state) => state.ordersPageReducer.activeOrder,
	);
	const valueModalMultyFiltersTabClosed = useTypedSelector(
		(state) =>
			state.ordersPageReducer.filters[ORDERS_TYPES.CLOSED].byMultyFilters,
	);

	const [isOpenArchiveFilterModal, setOpenArchiveFilterModal] =
		useState<boolean>(false);

	const isFilterActive = useMemo(
		() => !isEqual(DEFAUT_VALUEE_FILTERS, valueModalMultyFiltersTabClosed),
		[valueModalMultyFiltersTabClosed],
	);

	const isClosedTab = useMemo(
		() => ordersType === ORDERS_TYPES.CLOSED,
		[ordersType],
	);
	const isNotClosedTab = useMemo(() => !isClosedTab, [isClosedTab]);
	const isOwn = useMemo(() => activeOrder?.isOwn, [activeOrder]);
	const lastReceivedOrder = useMemo(() => {
		const value =
			activeOrder?.additionalFields?.transfer?.lastReceivedOrder;
		return value;
	}, [activeOrder]);
	const taxiService = useMemo(() => activeOrder.taxiService, [activeOrder]);
	const executor = useMemo(
		() => activeOrder?.executorToOrder?.[0]?.executor,
		[activeOrder?.executorToOrder],
	);

	const hasExecutorFromTransfer = useMemo(
		() => lastReceivedOrder?.executorToOrder?.length,
		[lastReceivedOrder?.executorToOrder],
	);

	const taxiServiceId = useMemo(() => taxiService?.id, [taxiService?.id]);

	const settlement = useMemo(
		() => taxiService?.settlement?.[language],
		[language, taxiService],
	);

	const company = useMemo(
		() => taxiService?.company?.name?.[language],
		[language, taxiService],
	);

	const executorTaxiServiceId = useMemo(
		() => executor?.taxiService?.id,
		[executor?.taxiService?.id],
	);

	const executorSettlement = useMemo(
		() => executor?.taxiService?.settlement?.[language],
		[executor?.taxiService?.settlement, language],
	);

	const executorCompany = useMemo(
		() => executor?.taxiService?.company?.name?.[language],
		[executor?.taxiService?.company?.name, language],
	);

	const canWorkingWithOrder = useMemo(() => {
		if (!isOwn) {
			const list = [
				taxiServiceId === executorTaxiServiceId,
				settlement === executorSettlement,
				company === executorCompany,
			];
			return !list.every((item) => item === true);
		}
		return false;
	}, [
		company,
		executorCompany,
		executorSettlement,
		executorTaxiServiceId,
		isOwn,
		settlement,
		taxiServiceId,
	]);

	const canEditOrder = useMemo(() => "id" in activeOrder, [activeOrder]);

	const canCloseOrder = useMemo(
		() => isNotClosedTab && "id" in activeOrder,
		[activeOrder, isNotClosedTab],
	);

	const canCopyOrder = useMemo(() => "id" in activeOrder, [activeOrder]);

	const canSetOrderExecutor = useMemo(
		() =>
			isNotClosedTab &&
			"id" in activeOrder &&
			activeOrder.status !== "executing",
		[activeOrder, isNotClosedTab],
	);

	const canApproveOrder = useMemo(
		() =>
			isNotClosedTab &&
			"id" in activeOrder &&
			isObject(activeOrder.offer) &&
			"executor" in activeOrder.offer,
		[activeOrder, isNotClosedTab],
	);

	const canSendOrderAgain = useMemo(() => false, []);

	const canRecalculateOrder = useMemo(
		() => isNotClosedTab && "id" in activeOrder,
		[activeOrder, isNotClosedTab],
	);

	const canReturnOrderOnAirOrRemoveExecutor = useMemo(() => {
		if (
			isOwn &&
			hasExecutorFromTransfer &&
			"id" in activeOrder &&
			activeOrder.status === "executing"
		) {
			return false;
		}

		return "id" in activeOrder && activeOrder.status === "executing";
	}, [activeOrder, hasExecutorFromTransfer, isOwn]);

	const canSendOrderToAir = useMemo(
		() => "id" in activeOrder && activeOrder.status === OrderStatus.CREATED,
		[activeOrder],
	);

	const canSendOrderToExecuting = useMemo(() => {
		if (!activeOrder || !moveToProgress) return false;

		const { stage } = activeOrder;

		const isExecutable = stage === Order.ORDERS_STAGE.EXECUTABLE;
		const isLife = stage === Order.ORDERS_STAGE.LIVE;

		// only for hot and preview tabs
		if (isExecutable) return false;

		const { ownOrderOwnCar, ownOrderForeignCar, foreignOrderOwnCar } =
			moveToProgress;

		const hasOwnExecutor =
			Array.isArray(activeOrder.executorToOrder) &&
			activeOrder.executorToOrder.length > 0;
		const hasNotOwnExecutor = hasExecutorFromTransfer;
		const isNotOwn = !isOwn;

		const differentOnFilterForTab = (type: MoveToProgressType): boolean =>
			isLife
				? type !== MoveToProgressType.NEVER
				: type === MoveToProgressType.MANUAL;

		const [
			isOwnOrderAndOwnExec,
			isOwnOrderAndNotOwnExec,
			isNotOwnOrderAndOwnExec,
		] = [
			!!(isOwn && hasOwnExecutor) &&
				differentOnFilterForTab(ownOrderOwnCar.type),
			!!(isOwn && hasNotOwnExecutor) &&
				differentOnFilterForTab(ownOrderForeignCar.type),
			!!(isNotOwn && hasOwnExecutor) &&
				differentOnFilterForTab(foreignOrderOwnCar.type),
		];

		if (isOwnOrderAndOwnExec) return true;
		if (isOwnOrderAndNotOwnExec) return true;
		if (isNotOwnOrderAndOwnExec) return true;

		return false;
	}, [activeOrder, hasExecutorFromTransfer, isOwn, moveToProgress]);

	const revertOrder = useMemo(
		() =>
			"id" in activeOrder &&
			activeOrder.status === OrderStatus.CLOSED &&
			activeOrder?.executorToOrder?.length,
		[activeOrder],
	);

	const canToggleOrderAir = useMemo(
		() =>
			"id" in activeOrder &&
			(activeOrder.status === OrderStatus.FREE_WAVE ||
				activeOrder.status === OrderStatus.CREATED),
		[activeOrder],
	);

	// const canSetOrderAir = useMemo(() => "id" in activeOrder, [activeOrder]);

	const canSetCallToClientBeingRequired = useMemo(
		() => "id" in activeOrder,
		[activeOrder],
	);

	const canSetCallToClientSuccess = useMemo(
		() => "id" in activeOrder,
		[activeOrder],
	);

	const canSetCallToExecutorBeingRequired = useMemo(
		() => "id" in activeOrder,
		[activeOrder],
	);

	const canSetCallToExecutorSuccess = useMemo(
		() => "id" in activeOrder,
		[activeOrder],
	);

	const canShowClientNumberToExecutor = useMemo(
		() => "id" in activeOrder,
		[activeOrder],
	);

	const isShowClientNumberToExecutorActive = useMemo(
		() =>
			("id" in activeOrder &&
				activeOrder.additionalFields?.displaySettings
					?.showCustomerPhone) ??
			false,
		[activeOrder],
	);

	const order = useMemo(() => activeOrder, [activeOrder]);

	const filterOrdersModalByMultyFiltersOnSave = useCallback(
		(data: SecondRow.ValueModalFilter) => {
			dispatch(
				orderPage.actions.setFiltersByMultyFilters({
					type: ordersType,
					data,
				}),
			);
		},
		[dispatch, ordersType],
	);

	useKeyBind(["Enter"], (event) => {
		event.preventDefault();
		event.stopPropagation();

		if (canSendOrderToAir) {
			onSendOrderToAir();

			return;
		}

		if (canApproveOrder) {
			onApproveOrder();

			return;
		}

		if (canSetOrderExecutor) {
			onSetOrderExecutorClick();

			return;
		}

		if (canSendOrderToExecuting) {
			onSendOrderToExecuting();
		}
	});

	return (
		<StyledGrid
			areas=""
			columns="1fr auto"
			alignItems="center"
			gap="5px"
			p="6px 8px"
		>
			<StyledRow alignItems="center" gap="5px">
				<Button
					variant="primary"
					title={
						t(
							"pages.mainPage.pages.orders.orderHeader.secondRow.str0",
						) ?? ""
					}
					settingsKeyId="newOrder"
					tabIndex={-1}
					onClick={toggleModalStatus}
					accessName={AccessKey.NEW_ORDER}
				>
					<Icon id="orders-add" size={20} />
				</Button>
				<Divider
					orientation="vertical"
					size={18}
					color="rgba(0, 0, 0, 0.12)"
				/>
				<Button
					disabled={!canEditOrder}
					variant="secondary"
					settingsKeyId="editOrder"
					title={
						t(
							"pages.mainPage.pages.orders.orderHeader.secondRow.str1",
						) ?? ""
					}
					tabIndex={-1}
					onClick={onOpenEditOrderModal}
					accessName={AccessKey.EDIT_ORDER}
				>
					<Icon id="orders-edit" size={20} />
				</Button>
				{isClosedTab && (
					<Button
						disabled={!revertOrder}
						variant="secondary"
						title={
							t(
								"pages.mainPage.pages.orders.orderHeader.secondRow.str2",
							) ?? ""
						}
						tabIndex={-1}
						onClick={onRevertOrder}
						accessName={AccessKey.REVERT_ORDER}
					>
						<Icon id="return-back" size={17} />
					</Button>
				)}
				{isNotClosedTab && (
					<Button
						disabled={!canCloseOrder || canWorkingWithOrder}
						variant="secondary"
						settingsKeyId="closeOrder"
						title={
							t(
								"pages.mainPage.pages.orders.orderHeader.secondRow.str3",
							) ?? ""
						}
						tabIndex={-1}
						onClick={onCloseOrderClick}
						accessName={AccessKey.CLOSED_ORDER}
					>
						<Icon id="orders-close" size={20} />
					</Button>
				)}

				<Button
					disabled={!canCopyOrder || canWorkingWithOrder}
					variant="secondary"
					settingsKeyId="copyOrder"
					title={
						t(
							"pages.mainPage.pages.orders.orderHeader.secondRow.str4",
						) ?? ""
					}
					tabIndex={-1}
					onClick={onCloneOrderClick}
					accessName={AccessKey.COPY_ORDER}
				>
					<Icon id="orders-copy" size={20} />
				</Button>

				{isNotClosedTab && (
					<Button
						disabled={!canSetOrderExecutor}
						variant="secondary"
						settingsKeyId="setOrderExecutor"
						title={(keys) =>
							`${
								t(
									"pages.mainPage.pages.orders.orderHeader.secondRow.str200",
								) ?? ""
							}${keys.join("+")}/Enter)`
						}
						tabIndex={-1}
						onClick={onSetOrderExecutorClick}
						accessName={AccessKey.SET_ORDER_EXECUTOR}
					>
						<Icon id="orders-executor-enter" size={20} />
					</Button>
				)}
				{isNotClosedTab && (
					<Button
						disabled={!canApproveOrder}
						variant="secondary"
						title={
							t(
								"pages.mainPage.pages.orders.orderHeader.secondRow.str201",
							) ?? ""
						}
						tabIndex={-1}
						onClick={onApproveOrder}
						accessName={AccessKey.APPROVE_ORDER}
					>
						<Icon id="orders-executor-check" size={20} />
					</Button>
				)}
				{isNotClosedTab && (
					<Button
						disabled={!canSendOrderAgain}
						variant="secondary"
						// settingsKeyId="sendOrderAgain"
						tabIndex={-1}
						title={
							t(
								"pages.mainPage.pages.orders.orderHeader.secondRow.str5",
							) ?? ""
						}
						accessName={AccessKey.SEND_ORDER_AGAIN}
					>
						<Icon id="orders-executor-refresh" size={20} />
					</Button>
				)}
				{isNotClosedTab && (
					<Button
						disabled={!canRecalculateOrder || canWorkingWithOrder}
						variant="secondary"
						settingsKeyId="recalculateOrder"
						title={
							t(
								"pages.mainPage.pages.orders.orderHeader.secondRow.str6",
							) ?? ""
						}
						tabIndex={-1}
						onClick={onRecalculateOrder}
						accessName={AccessKey.RECALCULATE_ORDER}
					>
						<Icon id="orders-recalculate" size={20} />
					</Button>
				)}
				{isNotClosedTab && (
					<Button
						disabled={!canRecalculateOrder || canWorkingWithOrder}
						variant="secondary"
						settingsKeyId="recalculateOrderWithActualRates"
						title={
							t(
								"pages.mainPage.pages.orders.orderHeader.secondRow.str7",
							) ?? ""
						}
						tabIndex={-1}
						onClick={onRecalculateOrderWithActualRates}
						accessName={
							AccessKey.RECALCULATE_ORDER_WITH_ACTUAL_RATES
						}
					>
						<Icon id="orders-recalculate" size={20} />
					</Button>
				)}
				{isNotClosedTab && (
					<Divider
						orientation="vertical"
						size={18}
						color="rgba(0, 0, 0, 0.12)"
					/>
				)}
				{isNotClosedTab && (
					<Button
						disabled={!canReturnOrderOnAirOrRemoveExecutor}
						variant="secondary"
						settingsKeyId="returnOrderOnAirOrRemoveExecutor"
						title={
							t(
								"pages.mainPage.pages.orders.orderHeader.secondRow.str202",
							) ?? ""
						}
						tabIndex={-1}
						onClick={onRefuseOrder}
						accessName={
							AccessKey.RETURN_ORDER_ON_AIR_OR_REMOVE_EXECUTOR
						}
					>
						<Icon id="orders-executor-remove" size={20} />
					</Button>
				)}
				{isNotClosedTab && (
					<Button
						disabled={!canSendOrderToAir}
						variant="secondary"
						title={
							t(
								"pages.mainPage.pages.orders.orderHeader.secondRow.str203",
							) ?? ""
						}
						tabIndex={-1}
						onClick={onSendOrderToAir}
						accessName={AccessKey.SEND_ORDER_TO_AIR}
					>
						<Icon id="orders-table-pivot" size={20} />
					</Button>
				)}
				{isNotClosedTab && (
					<Button
						disabled={!canSendOrderToExecuting}
						variant="secondary"
						title={(keys) =>
							`${
								t(
									"pages.mainPage.pages.orders.orderHeader.secondRow.str204",
								) ?? ""
							}${keys.join("+")}Enter)`
						}
						tabIndex={-1}
						onClick={onSendOrderToExecuting}
						accessName={AccessKey.SEND_ORDER_TO_EXECUTING}
					>
						<Icon id="orders-return" size={20} />
					</Button>
				)}
				{isNotClosedTab && (
					<Button
						disabled={!canToggleOrderAir}
						variant="secondary"
						settingsKeyId="toggleOrderAir"
						title={
							t(
								"pages.mainPage.pages.orders.orderHeader.secondRow.str205",
							) ?? ""
						}
						tabIndex={-1}
						onClick={onToggleOrderAir}
						accessName={AccessKey.TOGGLE_ORDER_AIR}
					>
						<Icon id="orders-table-eye" size={20} />
					</Button>
				)}
				{isNotClosedTab && <ColorButton />}
				{isNotClosedTab && (
					<Divider
						orientation="vertical"
						size={18}
						color="rgba(0, 0, 0, 0.12)"
					/>
				)}
				{isNotClosedTab && (
					<Button
						disabled={
							!canSetCallToClientBeingRequired ||
							canWorkingWithOrder
						}
						variant="secondary"
						settingsKeyId="setOrderCallToClientBeingRequired"
						title={
							t(
								"pages.mainPage.pages.orders.orderHeader.secondRow.str8",
							) ?? ""
						}
						tabIndex={-1}
						onClick={onSetCallToClientBeingRequired}
						accessName={
							AccessKey.SET_ORDER_CALL_TO_CLIENT_BEING_REQUIRED
						}
					>
						<Icon id="orders-client-call" size={20} />
					</Button>
				)}
				{isNotClosedTab && <CallClientButton />}
				{isNotClosedTab && (
					<Button
						disabled={
							!canSetCallToClientSuccess || canWorkingWithOrder
						}
						variant="secondary"
						settingsKeyId="setOrderCallToClientSuccess"
						title={
							t(
								"pages.mainPage.pages.orders.orderHeader.secondRow.str9",
							) ?? ""
						}
						tabIndex={-1}
						onClick={onSetCallToClientSuccess}
						accessName={AccessKey.SET_ORDER_CALL_TO_CLIENT_SUCCESS}
					>
						<Icon id="orders-client-call-mark" size={20} />
					</Button>
				)}
				{isNotClosedTab && (
					<Button
						disabled={
							!canSetCallToExecutorBeingRequired ||
							canWorkingWithOrder
						}
						variant="secondary"
						settingsKeyId="setOrderCallToExecutorBeingRequired"
						title={
							t(
								"pages.mainPage.pages.orders.orderHeader.secondRow.str10",
							) ?? ""
						}
						tabIndex={-1}
						onClick={onSetCallToExecutorBeingRequired}
						accessName={
							AccessKey.SET_ORDER_CALL_TO_EXECUTOR_BEING_REQUIRED
						}
					>
						<Icon id="orders-executor-call" size={20} />
					</Button>
				)}
				{isNotClosedTab && <CallExecutorButton />}
				{isNotClosedTab && (
					<Button
						disabled={
							!canSetCallToExecutorSuccess || canWorkingWithOrder
						}
						variant="secondary"
						settingsKeyId="setOrderCallToExecutorSuccess"
						title={
							t(
								"pages.mainPage.pages.orders.orderHeader.secondRow.str11",
							) ?? ""
						}
						tabIndex={-1}
						onClick={onSetCallToExecutorSuccess}
						accessName={
							AccessKey.SET_ORDER_CALL_TO_EXECUTOR_SUCCESS
						}
					>
						<Icon id="orders-executor-call-mark" size={20} />
					</Button>
				)}
				{isNotClosedTab && <AutoCallClientButton />}
				{isNotClosedTab && (
					<Button
						disabled={
							!canShowClientNumberToExecutor ||
							canWorkingWithOrder
						}
						active={isShowClientNumberToExecutorActive}
						variant="secondary"
						title={
							t(
								"pages.mainPage.pages.orders.orderHeader.secondRow.str13",
							) ?? ""
						}
						tabIndex={-1}
						onClick={onShowClientNumberToExecutor}
						accessName={AccessKey.SHOW_CLIENT_NUMBER_TO_EXECUTOR}
					>
						<Icon
							id="orders-show-client-number-to-executor"
							size={20}
						/>
					</Button>
				)}
				{isNotClosedTab && (
					<Divider
						orientation="vertical"
						size={18}
						color="rgba(0, 0, 0, 0.12)"
					/>
				)}
				{isNotClosedTab && <SMSButton />}
				{isNotClosedTab && <PaymentLinkSmsButton />}
				{isNotClosedTab && (
					<Divider
						orientation="vertical"
						size={18}
						color="rgba(0, 0, 0, 0.12)"
					/>
				)}
				{isNotClosedTab && (
					<OrderTransferButtons
						orderId={order?.id}
						disabled={!order.id}
						order={order}
					/>
				)}
			</StyledRow>
			<StyledRow alignItems="center">
				{isClosedTab && (
					<Button
						variant={isFilterActive ? "primary" : "secondary"}
						title={t("accessText.orderExtendedFilter") ?? ""}
						tabIndex={-1}
						onClick={() => {
							setOpenArchiveFilterModal(true);
						}}
						accessName={AccessKey.ORDER_EXTENDED_FILTER}
					>
						<Icon id="filters" size={20} />
					</Button>
				)}
			</StyledRow>
			<ArchiveOrderFiltersModalMemo
				// onChange={setValueModal}
				onChange={filterOrdersModalByMultyFiltersOnSave}
				value={valueModalMultyFiltersTabClosed}
				isOpenArchiveFilterModal={isOpenArchiveFilterModal}
				setOpenArchiveFilterModal={setOpenArchiveFilterModal}
				accessSection={[
					AccessKey.ORDERS,
					AccessKey.ORDER_EXTENDED_FILTER,
				]}
			/>
		</StyledGrid>
	);
};

declare namespace SecondRow {
	interface Props {
		onOpenEditOrderModal: VoidFunction;
		toggleModalStatus: VoidFunction;

		onCloseOrderClick: VoidFunction;
		onCloneOrderClick: VoidFunction;
		onSetOrderExecutorClick: VoidFunction;
		onApproveOrder: VoidFunction;
		onRecalculateOrder: VoidFunction;
		onRecalculateOrderWithActualRates: VoidFunction;
		onRefuseOrder: VoidFunction;
		onSendOrderToAir: VoidFunction;
		onToggleOrderAir: VoidFunction;
		onSetCallToClientBeingRequired: VoidFunction;
		onSetCallToClientSuccess: VoidFunction;
		onSetCallToExecutorBeingRequired: VoidFunction;
		onSetCallToExecutorSuccess: VoidFunction;
		onShowClientNumberToExecutor: VoidFunction;
		onSendOrderToExecuting: VoidFunction;
		onRevertOrder: VoidFunction;
	}

	interface ValueModalFilter extends ArchiveOrderFiltersModal.Value {}
}

export default SecondRow;
